<template>
  <div
    ref="parent"
    ref-cy="popover"
    class="c-popover"
    :class="{ '-bottom': !target, '-top': position === 'top' }"
    :style="style"
  >
    <slot />
  </div>
</template>

<script>
import { COMPONENT_HOOKS } from '@convenia/utils/modules/vue3Migration'

const getParents = ({ parentElement: parent }) => parent ? [ parent, ...getParents(parent) ] : []

const checkPlacement = (types, p) => typeof p === 'string' && types.indexOf(p) > -1

const getTargetElement = (target) => target?.$el || target || null

/**
 * Barebones popover component.
 */
export default {
  name: 'CPopover',

  props: {
    /**
     * The target of the popover, the popover will be positioned relative
     * to the target when open.
     * When the popover has no explicit target, it will be
     * positioned at the bottom of the screen.
     */
    target: {
      type: [ Object, Element ],
      default: null,
    },

    /**
     * Fine tuning of the popover's arrow horizontal position.
     */
    arrowSpacing: {
      type: [ Number, String ],
      default: 15
    },

    /**
     * Determines the distance between the target element and the popover arrow.
     */
    elementSpacing: {
      type: [ Number, String ],
      default: 0
    },

    /**
     * Whether to show the popover below or above the target element.
     */
    position: {
      type: String,
      default: 'bottom',
      validator: p => checkPlacement([ 'bottom', 'top' ], p)
    },

    /**
     * The horizontal alignment of the popover element relative to the target.
     */
    align: {
      type: String,
      default: 'center',
      validator: v => checkPlacement([ 'right', 'center', 'left' ], v)
    },

    /**
     * The element to set the scroll listener to, the popover closes when
     * scroll is triggered in this element. Defaults to the body element.
     */
    selector: {
      type: String,
      default: '',
    },

    /**
     * Sets the popover to automatically hide when the page content is scrolled
     */
    hideOnScroll: Boolean,

    /**
     * Not fresh click case
     */
    noFreshCheck: Boolean,

    customCoordinates: {
      type: Object,
      default: null
    },
  },

  emits: [ 'handler' ],

  data () {
    return {
      coordinates: {},
      fresh: true,
      arrowPosition: {}
    }
  },

  computed: {
    arrowAlignment () {
      if (this.align === 'left') return { '--right': `${this.arrowSpacing}px` }
      if (this.align === 'right') return { '--left': `${this.arrowSpacing}px` }
      return { '--left': `calc(50% - ${this.arrowSpacing / 2}px)` }
    },

    style () {
      return {
        ...(this.customCoordinates || this.coordinates),
        ...this.arrowAlignment,
        ...this.arrowPosition,
      }
    }
  },

  watch: {
    target: {
      handler: 'setPosition',
      immediate: true
    }
  },

  methods: {
    setPosition () {
      if (!this.target) return

      this.$nextTick(() => {
        const popover = this.$el.getBoundingClientRect()
        const targetElement = getTargetElement(this.target)

        if (!targetElement || typeof targetElement.getBoundingClientRect !== 'function') return

        const target = targetElement.getBoundingClientRect()

        // set top position
        const top = this.position === 'top'
          ? target.top - (8 + popover.height) - parseInt(this.elementSpacing)
          : target.top + (8 + target.height) + parseInt(this.elementSpacing)

        // set left position
        let left = target.left + (target.width / 2)
        if (this.align === 'left') left = left - popover.width + 20
        if (this.align === 'right') left -= 20
        if (this.align === 'center') left -= popover.width / 2

        // clamp within viewport with 8px margin
        const margin = 8
        const clampedLeft = Math.max(margin,
          Math.min(left, window.innerWidth - popover.width - margin))

        // set coordinates
        this.coordinates = { top: top + 'px', left: clampedLeft + 'px' }

        // set arrow position
        const arrowTop = this.position === 'bottom' ? '-5' : popover.height - 6
        const arrowStyle = { '--top': arrowTop + 'px' }

        // if the box was shifted by clamping, move the arrow so it still points at the target
        if (clampedLeft !== left && this.align === 'center') {
          const targetCenter = target.left + target.width / 2
          const arrowHalfWidth = 8 // border-left + border-right each 8px

          const minArrowLeft = parseInt(this.arrowSpacing)
          const maxArrowLeft = popover.width - arrowHalfWidth * 2
          const centeredArrowLeft = targetCenter - clampedLeft - arrowHalfWidth

          const arrowLeft = Math.max(
            minArrowLeft,
            Math.min(centeredArrowLeft, maxArrowLeft)
          )

          arrowStyle['--left'] = `${arrowLeft}px`
        }

        this.arrowPosition = arrowStyle
      })
    },

    emissionHandler (e) {
      if (!this.target) return

      // Checks if the popover has just been opened.
      // This is needed when the popopver activation element
      // is not the same as the 'target' prop.
      if (!this.noFreshCheck && this.fresh) { this.fresh = false; return }

      const targets = e.target ? [ e.target, ...getParents(e.target) ] : []
      const isTarget = targets.includes(getTargetElement(this.target))
      const isOutside = !targets.includes(this.$el)

      /**
       * Event is emitted when the user clicks outside the popover, or
       * presses the ESC key (closes it, basically). This event really should
       * be called `close`, sorry :(
       * @event handler
       * @type {undefined}
       */
      if (!isTarget && isOutside) this.$emit('handler', e)
    },

    clickEscape (e) {
      if (e.key === 'Escape' || e.keyCode === 27) this.$emit('handler')
    },

    onScroll (e) {
      if (this.INTEGRATION_TEST) return

      const { parent } = this.$refs
      if (!parent) return

      if (parent.contains(e.target) || parent.isSameNode(e.target)) return
      if (this.hideOnScroll) this.$emit('handler', e)
      else this.setPosition()
    }
  },

  mounted () {
    const scrollElement = this.selector
      ? document.querySelector(this.selector) || document.body
      : document.body

    window.addEventListener('click', this.emissionHandler)
    window.addEventListener('resize', this.setPosition)
    document.addEventListener('keydown', this.clickEscape, true)
    scrollElement.addEventListener('scroll', this.onScroll, true)
    this.$nextTick(this.setPosition)
  },

  [COMPONENT_HOOKS.BEFORE_UNMOUNT] () {
    const scrollElement = this.selector
      ? document.querySelector(this.selector) || document.body
      : document.body

    window.removeEventListener('click', this.emissionHandler)
    window.removeEventListener('resize', this.setPosition)
    document.removeEventListener('keydown', this.clickEscape, true)
    scrollElement.removeEventListener('scroll', this.onScroll, true)
  },

  inject: [ 'INTEGRATION_TEST' ],
}
</script>

<style lang="scss">
$c-popover-arrow-width: 8px;
$c-popover-arrow-height: 7px;
$c-popover-background: #fff;
$c-popover-border: 1px solid black;

%arrow-effect {
  position: absolute;
  content: '';
  width: 0;
  height: 0;
}

%arrow {
  &::before {
    @extend %arrow-effect;
    z-index: inherit;
  }
}

%base {
  display: block;
  position: fixed;
  border-radius: 5px;
  box-shadow: var(--box-shadow);
  z-index: var(--z-index-4);
}

.c-popover {
  @extend %base;
  @extend %arrow;

  &::before {
    bottom: 100%;
    border-width: 6px;
    left: var(--left);
    right: var(--right);
    border-left: $c-popover-arrow-width solid transparent;
    border-right: $c-popover-arrow-width solid transparent;
    border-bottom: $c-popover-arrow-height solid $c-popover-background;
  }
}

.c-popover.-bottom {
  margin: 10px;
  bottom: 0;
  left: calc(50% - 10px);
  transform: translateX(-50%);
  width: 100%;
  max-width: 300px;

  &::before {
    border-bottom: none;
  }
}

.c-popover.-top {
  &::before {
    top: 100%;
    border-bottom: none;
    border-top: $c-popover-arrow-height solid $c-popover-background;
  }

  &:not(.-dark)::before {
    opacity: 0.9;
  }
}
</style>
